/**
 * Portal BARUCH WAVE — app React independiente.
 *
 * Auth con Firebase (email/password + Google). El backend valida el token
 * contra la tabla `usuarios_socios` y filtra las reservas por el paquete
 * autorizado. Sin precios ni info financiera — solo datos operativos.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  onAuthStateChanged, signInWithEmailAndPassword, signInWithPopup, signOut,
} from 'firebase/auth';
import {
  Box, Typography, TextField, Button, Alert, CircularProgress, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  ThemeProvider, createTheme, CssBaseline, IconButton, Tooltip, Card,
  MenuItem, InputAdornment,
  ToggleButton, ToggleButtonGroup, Drawer, Select, FormControl, InputLabel, Divider,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import LogoutIcon from '@mui/icons-material/Logout';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PendingIcon from '@mui/icons-material/Pending';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TableRowsIcon from '@mui/icons-material/TableRows';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import HotelIcon from '@mui/icons-material/Hotel';
import EventNoteIcon from '@mui/icons-material/EventNote';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import TodayIcon from '@mui/icons-material/Today';
import DirectionsBoatFilledIcon from '@mui/icons-material/DirectionsBoatFilled';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import { auth, googleProvider } from './firebase';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

const theme = createTheme({
  typography: { fontFamily: `'Inter', -apple-system, BlinkMacSystemFont, sans-serif` },
  palette: { primary: { main: '#0ea5e9' }, secondary: { main: '#0891b2' } },
});

function fmtFecha(iso) {
  if (!iso) return '—';
  try { return new Date(iso + 'T00:00:00').toLocaleDateString('es-CO', { weekday: 'short', day: '2-digit', month: 'short' }); }
  catch { return iso; }
}
function fmtHora(h) {
  if (!h) return '—';
  return String(h).slice(0, 5);
}
function fmtPax(r) {
  const parts = [];
  if (r.adultos) parts.push(`${r.adultos}A`);
  if (r.ninos) parts.push(`${r.ninos}N`);
  if (r.bebes) parts.push(`${r.bebes}B`);
  return parts.join(' · ') || '—';
}
function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function addDays(iso, days) {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const ESTADO_CHIP = {
  embarcado: { bg: '#dcfce7', color: '#166534', label: 'Embarcado' },
  pendiente: { bg: '#fef3c7', color: '#92400e', label: 'Pendiente' },
  no_show:   { bg: '#f1f5f9', color: '#475569', label: 'No-show' },
};


export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  if (authLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {user ? <Dashboard user={user} /> : <Login />}
    </ThemeProvider>
  );
}


function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const doEmail = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try { await signInWithEmailAndPassword(auth, email.trim(), password); }
    catch (ex) { setErr(traducirError(ex)); }
    setBusy(false);
  };
  const doGoogle = async () => {
    setErr(''); setBusy(true);
    try { await signInWithPopup(auth, googleProvider); }
    catch (ex) { setErr(traducirError(ex)); }
    setBusy(false);
  };

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      p: 2,
    }}>
      <Card sx={{
        width: '100%', maxWidth: 420, p: { xs: 3, sm: 4 }, borderRadius: 4,
        boxShadow: '0 20px 60px rgba(14, 165, 233, 0.15)',
        background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(20px)',
      }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box sx={{
            width: 64, height: 64, borderRadius: 3, mx: 'auto', mb: 1.5,
            background: 'linear-gradient(135deg, #0ea5e9 0%, #0891b2 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 20px rgba(14, 165, 233, 0.35)',
          }}>
            <DirectionsBoatIcon sx={{ fontSize: 34, color: '#fff' }} />
          </Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0c4a6e', letterSpacing: 0.5 }}>
            BARUCH WAVE
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.5 }}>
            Portal de reservas del socio
          </Typography>
        </Box>

        <form onSubmit={doEmail}>
          <TextField fullWidth label="Correo" type="email" size="small"
            value={email} onChange={e => setEmail(e.target.value)}
            sx={{ mb: 1.5 }} autoComplete="email" />
          <TextField fullWidth label="Contraseña" type="password" size="small"
            value={password} onChange={e => setPassword(e.target.value)}
            sx={{ mb: 2 }} autoComplete="current-password" />
          {err && <Alert severity="error" sx={{ mb: 1.5, fontSize: 12 }}>{err}</Alert>}
          <Button type="submit" fullWidth variant="contained" disabled={busy || !email || !password}
            sx={{
              textTransform: 'none', fontWeight: 700, py: 1.2,
              background: 'linear-gradient(135deg, #0ea5e9 0%, #0891b2 100%)',
              boxShadow: '0 8px 20px rgba(14, 165, 233, 0.35)',
            }}>
            {busy ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Iniciar sesión'}
          </Button>
        </form>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2 }}>
          <Box sx={{ flex: 1, height: 1, bgcolor: '#e2e8f0' }} />
          <Typography sx={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>O</Typography>
          <Box sx={{ flex: 1, height: 1, bgcolor: '#e2e8f0' }} />
        </Box>

        <Button fullWidth variant="outlined" startIcon={<GoogleIcon />}
          onClick={doGoogle} disabled={busy}
          sx={{ textTransform: 'none', fontWeight: 600, py: 1.1, color: '#334155', borderColor: '#cbd5e1' }}>
          Continuar con Google
        </Button>
      </Card>
    </Box>
  );
}


function traducirError(ex) {
  const c = ex?.code || '';
  if (c.includes('user-not-found') || c.includes('invalid-credential') || c.includes('wrong-password'))
    return 'Correo o contraseña incorrectos.';
  if (c.includes('too-many-requests'))
    return 'Demasiados intentos. Espera un momento y vuelve a intentar.';
  if (c.includes('network'))
    return 'Sin conexión — verifica tu internet.';
  return ex?.message || 'No se pudo iniciar sesión.';
}


function Dashboard({ user }) {
  const [info, setInfo] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fechaDesde, setFechaDesde] = useState(todayLocal());
  const [fechaHasta, setFechaHasta] = useState(addDays(todayLocal(), 30));
  const [preset, setPreset] = useState('mes');
  const [search, setSearch] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');   // '' | 'embarcado' | 'no_show' | 'pendiente'
  const [filtroTamano, setFiltroTamano] = useState('');   // '' | 'individual' | 'pareja' | 'grupo' | 'grupo_grande'
  const [vista, setVista] = useState('tabla');            // 'tabla' | 'calendario'
  const [detalleId, setDetalleId] = useState(null);       // id de reserva abierta en el drawer

  const authedFetch = async (path) => {
    const token = await user.getIdToken();
    const res = await fetch(`${API_BASE}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail || `Error ${res.status}`);
    }
    return res.json();
  };

  const cargar = async () => {
    setLoading(true); setError('');
    try {
      if (!info) {
        const i = await authedFetch('/socios/mi-info');
        setInfo(i);
      }
      const q = new URLSearchParams({ fecha_desde: fechaDesde, fecha_hasta: fechaHasta });
      if (filtroEstado) q.append('estado_embarque', filtroEstado);
      if (filtroTamano) q.append('tamano_grupo', filtroTamano);
      const d = await authedFetch(`/socios/reservas?${q}`);
      setData(d);
    } catch (ex) {
      setError(ex.message || 'Error cargando datos');
    }
    setLoading(false);
  };

  useEffect(() => { cargar(); }, [fechaDesde, fechaHasta, filtroEstado, filtroTamano]); // eslint-disable-line

  const aplicarPreset = (p) => {
    const hoy = todayLocal();
    setPreset(p);
    if (p === 'hoy') { setFechaDesde(hoy); setFechaHasta(hoy); }
    else if (p === 'semana') { setFechaDesde(hoy); setFechaHasta(addDays(hoy, 7)); }
    else if (p === 'mes') { setFechaDesde(hoy); setFechaHasta(addDays(hoy, 30)); }
    else if (p === 'pasado') { setFechaDesde(addDays(hoy, -30)); setFechaHasta(hoy); }
  };

  const reservasFiltradas = useMemo(() => {
    const list = data?.reservas || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(r =>
      (r.nombre_cliente || '').toLowerCase().includes(q) ||
      (r.codigo_reserva || '').toLowerCase().includes(q) ||
      (r.telefono || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  if (error && error.toLowerCase().includes('acceso')) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', minHeight: '100vh' }}>
        <Alert severity="warning" sx={{ maxWidth: 500, mx: 'auto' }}>
          {error}
        </Alert>
        <Button onClick={() => signOut(auth)} sx={{ mt: 2, textTransform: 'none' }}>
          Cerrar sesión
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* Top bar */}
      <Box sx={{
        px: { xs: 2, md: 4 }, py: 1.5,
        background: 'linear-gradient(135deg, #0c4a6e 0%, #0e7490 100%)',
        color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 2,
          background: 'rgba(255,255,255,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <DirectionsBoatIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 800, letterSpacing: 0.5, lineHeight: 1.1 }}>
            {info?.paquete?.nombre || 'BARUCH WAVE'}
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#bae6fd' }}>
            Portal del socio · {info?.socio?.nombre || info?.socio?.email || user.email}
          </Typography>
        </Box>
        <Tooltip title="Recargar">
          <IconButton onClick={cargar} sx={{ color: '#fff' }}><RefreshIcon /></IconButton>
        </Tooltip>
        <Tooltip title="Cerrar sesión">
          <IconButton onClick={() => signOut(auth)} sx={{ color: '#fff' }}><LogoutIcon /></IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* KPIs */}
        <Box sx={{
          display: 'grid', gap: 1.5, mb: 2,
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
        }}>
          <KpiCard icon={<PendingIcon />} title="Reservas" value={data?.kpis?.total_reservas ?? '—'} color="#0ea5e9" />
          <KpiCard icon={<GroupsIcon />} title="Pax total" value={data?.kpis?.pax_total ?? '—'} color="#0891b2" />
          <KpiCard icon={<CheckCircleOutlineIcon />} title="Embarcados" value={data?.kpis?.embarcados ?? '—'} color="#16a34a" />
          <KpiCard icon={<PendingIcon />} title="Pendientes" value={data?.kpis?.pendientes ?? '—'} color="#f59e0b" />
        </Box>

        {/* Toggle vista + botones de descarga */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <ToggleButtonGroup size="small" value={vista} exclusive
            onChange={(_, v) => v && setVista(v)}
            sx={{
              bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              '& .Mui-selected': { bgcolor: '#0ea5e9 !important', color: '#fff !important' },
            }}>
            <ToggleButton value="hoy"><TodayIcon sx={{ fontSize: 16, mr: 0.5 }} /> Hoy</ToggleButton>
            <ToggleButton value="48h"><EventNoteIcon sx={{ fontSize: 16, mr: 0.5 }} /> 48h</ToggleButton>
            <ToggleButton value="tabla"><TableRowsIcon sx={{ fontSize: 16, mr: 0.5 }} /> Lista</ToggleButton>
            <ToggleButton value="calendario"><CalendarMonthIcon sx={{ fontSize: 16, mr: 0.5 }} /> Calendario</ToggleButton>
            <ToggleButton value="analitica"><InsertChartIcon sx={{ fontSize: 16, mr: 0.5 }} /> Analítica</ToggleButton>
          </ToggleButtonGroup>

          {vista === 'tabla' && (
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              <BotonDescargar user={user} fmt="xlsx" icon={<TableChartIcon />}
                filtros={{ fecha_desde: fechaDesde, fecha_hasta: fechaHasta,
                           estado_embarque: filtroEstado, tamano_grupo: filtroTamano }} />
              <BotonDescargar user={user} fmt="pdf" icon={<PictureAsPdfIcon />}
                filtros={{ fecha_desde: fechaDesde, fecha_hasta: fechaHasta,
                           estado_embarque: filtroEstado, tamano_grupo: filtroTamano }} />
            </Box>
          )}
        </Box>

        {/* Filtros — solo cuando la vista es tabla */}
        {vista === 'tabla' && (
          <Card sx={{ p: 1.5, mb: 2, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
              {[
                { k: 'hoy',   label: 'Hoy' },
                { k: 'semana', label: 'Próximos 7 días' },
                { k: 'mes',    label: 'Próximos 30 días' },
                { k: 'pasado', label: 'Últimos 30 días' },
              ].map(p => (
                <Chip key={p.k} size="small" label={p.label} clickable
                  onClick={() => aplicarPreset(p.k)}
                  variant={preset === p.k ? 'filled' : 'outlined'}
                  sx={{
                    fontSize: 11, fontWeight: 700,
                    bgcolor: preset === p.k ? '#0ea5e9' : 'transparent',
                    color: preset === p.k ? '#fff' : '#0369a1',
                    borderColor: '#7dd3fc',
                  }} />
              ))}
            </Box>
            <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 2fr' } }}>
              <TextField type="date" size="small" label="Desde"
                InputLabelProps={{ shrink: true }} value={fechaDesde}
                onChange={e => { setPreset(''); setFechaDesde(e.target.value); }} />
              <TextField type="date" size="small" label="Hasta"
                InputLabelProps={{ shrink: true }} value={fechaHasta}
                onChange={e => { setPreset(''); setFechaHasta(e.target.value); }} />
              <TextField size="small" placeholder="Buscar por nombre, código o teléfono"
                value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }} />
            </Box>

            {/* Filtros adicionales */}
            <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr' }, mt: 1 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Estado embarque</InputLabel>
                <Select
                  label="Estado embarque"
                  value={filtroEstado}
                  onChange={e => setFiltroEstado(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="embarcado">Embarcado</MenuItem>
                  <MenuItem value="pendiente">Pendiente</MenuItem>
                  <MenuItem value="no_show">No-show</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Tamaño del grupo</InputLabel>
                <Select
                  label="Tamaño del grupo"
                  value={filtroTamano}
                  onChange={e => setFiltroTamano(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="individual">Individual (1 pax)</MenuItem>
                  <MenuItem value="pareja">Pareja (2 pax)</MenuItem>
                  <MenuItem value="grupo">Grupo (3-5 pax)</MenuItem>
                  <MenuItem value="grupo_grande">Grupo grande (6+ pax)</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Card>
        )}

        {/* Vista HOY EMBARCAMOS */}
        {vista === 'hoy' && (
          <HoyEmbarcamos
            authedFetch={authedFetch}
            onAbrirDetalle={(id) => setDetalleId(id)}
          />
        )}

        {/* Vista 48 HORAS */}
        {vista === '48h' && (
          <Proximas48h
            authedFetch={authedFetch}
            onAbrirDetalle={(id) => setDetalleId(id)}
          />
        )}

        {/* Vista Calendario */}
        {vista === 'calendario' && (
          <CalendarioSocio
            authedFetch={authedFetch}
            onDayClick={(iso) => {
              setPreset('');
              setFechaDesde(iso);
              setFechaHasta(iso);
              setVista('tabla');
            }}
          />
        )}

        {/* Vista ANALÍTICA */}
        {vista === 'analitica' && (
          <VistaAnalitica authedFetch={authedFetch} />
        )}

        {/* Vista Tabla — desktop: tabla / mobile: cards */}
        {vista === 'tabla' && (
          loading ? (
            <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress /></Box>
          ) : reservasFiltradas.length === 0 ? (
            <Card sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
              <DirectionsBoatIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#475569' }}>
                No hay reservas en este rango.
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 0.5 }}>
                Ajusta las fechas o revisa los presets de arriba.
              </Typography>
            </Card>
          ) : (
            <>
              {/* Desktop */}
              <TableContainer component={Paper} sx={{ borderRadius: 3, display: { xs: 'none', md: 'block' } }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: '#f1f5f9', fontWeight: 700, fontSize: 11, color: '#0f172a' } }}>
                      <TableCell>Código</TableCell>
                      <TableCell>Titular</TableCell>
                      <TableCell>Teléfono</TableCell>
                      <TableCell>Correo</TableCell>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Hora</TableCell>
                      <TableCell align="center">Pax</TableCell>
                      <TableCell align="center">Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reservasFiltradas.map(r => {
                      const est = ESTADO_CHIP[r.estado_embarque] || ESTADO_CHIP.pendiente;
                      return (
                        <TableRow key={r.id} hover
                          onClick={() => setDetalleId(r.id)}
                          sx={{ cursor: 'pointer' }}>
                          <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>{r.codigo_reserva}</TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{r.nombre_cliente}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: '#475569' }}>{r.telefono || '—'}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: '#475569', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={r.correo || ''}>
                            {r.correo || '—'}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{fmtFecha(r.fecha_salida)}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{fmtHora(r.hora_salida)}</TableCell>
                          <TableCell align="center" sx={{ fontSize: 12, fontWeight: 700, color: '#0891b2' }}>
                            {fmtPax(r)}
                          </TableCell>
                          <TableCell align="center">
                            <Chip size="small" label={est.label} sx={{ bgcolor: est.bg, color: est.color, fontWeight: 700, fontSize: 10, height: 20 }} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Mobile — cards */}
              <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1 }}>
                {reservasFiltradas.map(r => {
                  const est = ESTADO_CHIP[r.estado_embarque] || ESTADO_CHIP.pendiente;
                  return (
                    <Card key={r.id} sx={{ p: 1.5, borderRadius: 3, cursor: 'pointer' }}
                      onClick={() => setDetalleId(r.id)}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }} noWrap>{r.nombre_cliente}</Typography>
                          <Typography sx={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>{r.codigo_reserva}</Typography>
                        </Box>
                        <Chip size="small" label={est.label} sx={{ bgcolor: est.bg, color: est.color, fontWeight: 700, fontSize: 10, height: 20 }} />
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5, mt: 1 }}>
                        <Info label="Fecha" value={fmtFecha(r.fecha_salida)} />
                        <Info label="Hora" value={fmtHora(r.hora_salida)} />
                        <Info label="Pax" value={fmtPax(r)} highlight />
                      </Box>
                      {r.telefono && (
                        <Typography sx={{ fontSize: 12, color: '#475569', mt: 0.5 }}>
                          📱 {r.telefono}
                        </Typography>
                      )}
                    </Card>
                  );
                })}
              </Box>
            </>
          )
        )}

        {/* Drawer detalle reserva */}
        <DetalleReservaDrawer
          reservacionId={detalleId}
          authedFetch={authedFetch}
          onClose={() => setDetalleId(null)}
        />

        <Typography sx={{ mt: 3, textAlign: 'center', fontSize: 10, color: '#94a3b8' }}>
          BARUCH WAVE · Powered by NARA Tourism
        </Typography>
      </Box>
    </Box>
  );
}


function KpiCard({ icon, title, value, color }) {
  return (
    <Card sx={{ p: 1.5, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{
        width: 38, height: 38, borderRadius: 2,
        bgcolor: `${color}20`, color, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 20 } })}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>{title}</Typography>
        <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{value}</Typography>
      </Box>
    </Card>
  );
}


function Info({ label, value, highlight }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ fontSize: 12, fontWeight: highlight ? 800 : 600, color: highlight ? '#0891b2' : '#0f172a' }}>{value}</Typography>
    </Box>
  );
}


// ============================================================================
// CalendarioSocio — vista mensual de reservas
// ============================================================================
const MESES_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const DIAS_SEMANA = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

function CalendarioSocio({ authedFetch, onDayClick }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true); setError('');
    authedFetch(`/socios/calendario?year=${year}&month=${month}`)
      .then(d => { if (alive) setData(d); })
      .catch(ex => { if (alive) setError(ex.message || 'Error'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [year, month, authedFetch]);

  const cambiarMes = (delta) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  // Padding al inicio para alinear con lunes=1
  const offset = data ? (data.dias[0].dia_semana - 1) : 0;
  const cells = data ? [...Array(offset).fill(null), ...data.dias] : [];
  // Padding al final para completar filas de 7
  while (cells.length % 7 !== 0) cells.push(null);

  const hoyISO = todayLocal();

  return (
    <Card sx={{ p: 2, borderRadius: 3 }}>
      {/* Barra de navegación mes/año */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <IconButton onClick={() => cambiarMes(-1)}><ChevronLeftIcon /></IconButton>
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#0c4a6e' }}>
            {MESES_ES[month - 1]} {year}
          </Typography>
          {data && (
            <Typography sx={{ fontSize: 11, color: '#64748b' }}>
              {data.totales_mes.reservas} reserva{data.totales_mes.reservas !== 1 ? 's' : ''} · {data.totales_mes.pax} pax
            </Typography>
          )}
        </Box>
        <IconButton onClick={() => cambiarMes(1)}><ChevronRightIcon /></IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress /></Box>
      )}

      {!loading && data && (
        <>
          {/* Encabezado días semana */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.5 }}>
            {DIAS_SEMANA.map(d => (
              <Box key={d} sx={{ textAlign: 'center', fontSize: 10, fontWeight: 700,
                  color: '#64748b', letterSpacing: 0.5, py: 0.5 }}>
                {d}
              </Box>
            ))}
          </Box>

          {/* Grilla de días */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
            {cells.map((day, idx) => {
              if (!day) return <Box key={`empty-${idx}`} sx={{ minHeight: 78 }} />;
              const esHoy = day.fecha === hoyISO;
              const tieneReservas = day.reservas > 0;
              return (
                <Box key={day.fecha}
                  onClick={tieneReservas ? () => onDayClick(day.fecha) : undefined}
                  sx={{
                    minHeight: 78, p: 0.75, borderRadius: 1.5,
                    border: esHoy ? '2px solid #0ea5e9' : '1px solid #e2e8f0',
                    bgcolor: tieneReservas ? (esHoy ? '#e0f2fe' : '#f0f9ff') : '#fafafa',
                    cursor: tieneReservas ? 'pointer' : 'default',
                    display: 'flex', flexDirection: 'column',
                    transition: 'all 0.15s',
                    '&:hover': tieneReservas ? {
                      bgcolor: '#bae6fd', transform: 'translateY(-1px)',
                      boxShadow: '0 3px 8px rgba(14,165,233,0.25)',
                    } : {},
                  }}>
                  <Typography sx={{
                    fontSize: 12, fontWeight: 700,
                    color: esHoy ? '#0369a1' : (tieneReservas ? '#0c4a6e' : '#94a3b8'),
                    mb: 0.25,
                  }}>
                    {parseInt(day.fecha.slice(-2), 10)}
                  </Typography>
                  {tieneReservas ? (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#0891b2', lineHeight: 1.2 }}>
                        {day.pax} pax
                      </Typography>
                      <Typography sx={{ fontSize: 9.5, color: '#64748b', lineHeight: 1.2 }}>
                        {day.reservas} reserva{day.reservas !== 1 ? 's' : ''}
                      </Typography>
                      {(day.no_show > 0 || day.embarcados > 0) && (
                        <Box sx={{ display: 'flex', gap: 0.25, mt: 0.25 }}>
                          {day.embarcados > 0 && (
                            <Chip size="small" label={`✓${day.embarcados}`}
                              sx={{ bgcolor: '#dcfce7', color: '#166534', fontSize: 8, height: 14, fontWeight: 700 }} />
                          )}
                          {day.no_show > 0 && (
                            <Chip size="small" label={`✗${day.no_show}`}
                              sx={{ bgcolor: '#f1f5f9', color: '#475569', fontSize: 8, height: 14, fontWeight: 700 }} />
                          )}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ flex: 1 }} />
                  )}
                </Box>
              );
            })}
          </Box>

          <Typography sx={{ fontSize: 10.5, color: '#94a3b8', mt: 1.5, textAlign: 'center' }}>
            Click en un día con reservas para verlas en detalle
          </Typography>
        </>
      )}
    </Card>
  );
}


// ============================================================================
// DetalleReservaDrawer — panel lateral con toda la info de una reserva
// ============================================================================
function DetalleReservaDrawer({ reservacionId, authedFetch, onClose }) {
  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!reservacionId) { setDetalle(null); setError(''); return; }
    let alive = true;
    setLoading(true); setError('');
    authedFetch(`/socios/reservas/${reservacionId}`)
      .then(d => { if (alive) setDetalle(d); })
      .catch(ex => { if (alive) setError(ex.message || 'Error'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [reservacionId, authedFetch]);

  const est = detalle ? (ESTADO_CHIP[detalle.estado_embarque] || ESTADO_CHIP.pendiente) : null;

  return (
    <Drawer anchor="right" open={!!reservacionId} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 440 }, bgcolor: '#f8fafc' } }}>
      {/* Header */}
      <Box sx={{
        p: 2, background: 'linear-gradient(135deg, #0c4a6e 0%, #0e7490 100%)',
        color: '#fff', display: 'flex', alignItems: 'flex-start', gap: 1,
      }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 11, color: '#bae6fd', letterSpacing: 0.5, fontWeight: 700 }}>
            Detalle de reserva
          </Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 800, mt: 0.25 }}>
            {detalle?.codigo_reserva || '—'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ p: 2 }}>
        {loading && (
          <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress /></Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {detalle && (
          <>
            {/* Titular + estado */}
            <Card sx={{ p: 1.5, mb: 1.5, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                  {detalle.nombre_cliente}
                </Typography>
                {est && (
                  <Chip size="small" label={est.label}
                    sx={{ bgcolor: est.bg, color: est.color, fontWeight: 700, fontSize: 10 }} />
                )}
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5, mt: 1 }}>
                <Info label="Adultos" value={detalle.adultos} />
                <Info label="Niños" value={detalle.ninos} />
                <Info label="Bebés" value={detalle.bebes} />
              </Box>
              <Divider sx={{ my: 1 }} />
              <Typography sx={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase',
                  letterSpacing: 0.5, fontWeight: 700, mb: 0.5 }}>
                Origen
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0369a1' }}>
                {detalle.origen}
              </Typography>
            </Card>

            {/* Salida */}
            <Card sx={{ p: 1.5, mb: 1.5, borderRadius: 3 }}>
              <Typography sx={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase',
                  letterSpacing: 0.5, fontWeight: 700, mb: 1 }}>
                Salida
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Info label="Fecha" value={fmtFecha(detalle.fecha_salida)} />
                <Info label="Hora" value={fmtHora(detalle.hora_salida)} />
              </Box>
              {detalle.punto_embarque && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Info label="Punto de embarque" value={detalle.punto_embarque} />
                </>
              )}
            </Card>

            {/* Contacto */}
            {(detalle.telefono || detalle.correo || detalle.hotel_huesped) && (
              <Card sx={{ p: 1.5, mb: 1.5, borderRadius: 3 }}>
                <Typography sx={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase',
                    letterSpacing: 0.5, fontWeight: 700, mb: 1 }}>
                  Contacto
                </Typography>
                {detalle.telefono && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <PhoneIcon sx={{ fontSize: 16, color: '#0891b2' }} />
                    <Typography sx={{ fontSize: 13 }}>{detalle.telefono}</Typography>
                  </Box>
                )}
                {detalle.correo && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <EmailIcon sx={{ fontSize: 16, color: '#0891b2' }} />
                    <Typography sx={{ fontSize: 13 }}>{detalle.correo}</Typography>
                  </Box>
                )}
                {detalle.hotel_huesped && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HotelIcon sx={{ fontSize: 16, color: '#0891b2' }} />
                    <Typography sx={{ fontSize: 13 }}>{detalle.hotel_huesped}</Typography>
                  </Box>
                )}
              </Card>
            )}

            {/* Pasajeros individuales */}
            {detalle.viajeros && detalle.viajeros.length > 0 && (
              <Card sx={{ p: 1.5, mb: 1.5, borderRadius: 3 }}>
                <Typography sx={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase',
                    letterSpacing: 0.5, fontWeight: 700, mb: 1 }}>
                  Pasajeros ({detalle.viajeros.length})
                </Typography>
                {detalle.viajeros.map(v => (
                  <Box key={v.id} sx={{ display: 'flex', justifyContent: 'space-between',
                      py: 0.75, borderTop: '1px solid #f1f5f9', '&:first-of-type': { borderTop: 0 } }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                        {v.nombre_completo}
                      </Typography>
                      {(v.documento_tipo || v.documento_numero) && (
                        <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                          {v.documento_tipo || ''} {v.documento_numero || ''}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
                      {v.es_contacto_principal && (
                        <Chip size="small" label="Titular"
                          sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontSize: 9, height: 18, fontWeight: 700 }} />
                      )}
                      {v.es_menor && (
                        <Chip size="small" label="Menor"
                          sx={{ bgcolor: '#fef3c7', color: '#92400e', fontSize: 9, height: 18, fontWeight: 700 }} />
                      )}
                    </Box>
                  </Box>
                ))}
              </Card>
            )}

            {/* Notas y requerimientos */}
            {(detalle.notas || detalle.requerimientos_especiales || detalle.observaciones_agencia) && (
              <Card sx={{ p: 1.5, mb: 1.5, borderRadius: 3 }}>
                <Typography sx={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase',
                    letterSpacing: 0.5, fontWeight: 700, mb: 1 }}>
                  Notas y requerimientos
                </Typography>
                {detalle.requerimientos_especiales && (
                  <Box sx={{ mb: 1, p: 1, bgcolor: '#fef3c7', borderRadius: 1 }}>
                    <Typography sx={{ fontSize: 10, color: '#92400e', fontWeight: 700, mb: 0.25 }}>
                      REQUERIMIENTOS ESPECIALES
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#78350f' }}>
                      {detalle.requerimientos_especiales}
                    </Typography>
                  </Box>
                )}
                {detalle.observaciones_agencia && (
                  <Box sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 700, mb: 0.25 }}>
                      OBSERVACIONES AGENCIA
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#334155' }}>
                      {detalle.observaciones_agencia}
                    </Typography>
                  </Box>
                )}
                {detalle.notas && (
                  <Box>
                    <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 700, mb: 0.25 }}>
                      NOTAS
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#334155' }}>
                      {detalle.notas}
                    </Typography>
                  </Box>
                )}
              </Card>
            )}

            {/* Timeline */}
            {detalle.timeline && detalle.timeline.length > 0 && (
              <Card sx={{ p: 1.5, mb: 1.5, borderRadius: 3 }}>
                <Typography sx={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase',
                    letterSpacing: 0.5, fontWeight: 700, mb: 1 }}>
                  Historial
                </Typography>
                {detalle.timeline.map((t, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                    <EventNoteIcon sx={{ fontSize: 14, color: '#0891b2' }} />
                    <Typography sx={{ fontSize: 12, color: '#0f172a', flex: 1 }}>
                      {t.evento === 'reserva_creada' ? 'Reserva creada' :
                       t.evento === 'embarque' ? 'Embarcado' : t.evento}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                      {new Date(t.fecha).toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>
                ))}
              </Card>
            )}

            {/* Fotos check-in */}
            {detalle.checkins && detalle.checkins.length > 0 && (
              <Card sx={{ p: 1.5, mb: 1.5, borderRadius: 3 }}>
                <Typography sx={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase',
                    letterSpacing: 0.5, fontWeight: 700, mb: 1 }}>
                  Check-in DIMAR
                </Typography>
                {detalle.checkins.map(c => (
                  <Box key={c.id} sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>
                      {c.nombre_capturado
                        || (c.rol === 'titular' ? detalle.nombre_cliente : `Pasajero (${c.rol || 'sin rol'})`)}
                    </Typography>
                    {(c.documento_tipo || c.documento_numero) ? (
                      <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                        {[c.documento_tipo, c.documento_numero].filter(Boolean).join(' ')}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
                        Documento no capturado
                      </Typography>
                    )}
                    {c.foto_url && (
                      <Box sx={{ mt: 0.5, borderRadius: 1, overflow: 'hidden', border: '1px solid #e2e8f0',
                          cursor: 'pointer' }}
                        onClick={() => window.open(`${API_BASE}${c.foto_url}`, '_blank', 'noopener')}
                      >
                        <img
                          src={`${API_BASE}${c.foto_url}`}
                          alt="Documento del check-in"
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                          style={{ width: '100%', maxHeight: 300, objectFit: 'cover', display: 'block' }} />
                        <Box sx={{ display: 'none', p: 2, textAlign: 'center',
                            bgcolor: '#f8fafc', color: '#94a3b8', fontSize: 12 }}>
                          Foto no disponible
                        </Box>
                      </Box>
                    )}
                  </Box>
                ))}
              </Card>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
}


// ============================================================================
// BotonDescargar — descarga Excel/PDF con auth Firebase
// ============================================================================
function BotonDescargar({ user, fmt, icon, filtros }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const handleDownload = async () => {
    setBusy(true); setErr('');
    try {
      const token = await user.getIdToken();
      const qs = new URLSearchParams();
      Object.entries(filtros || {}).forEach(([k, v]) => {
        if (v) qs.append(k, v);
      });
      const path = fmt === 'xlsx' ? '/socios/reservas.xlsx' : '/socios/reservas.pdf';
      const url = `${API_BASE}${path}${qs.toString() ? '?' + qs : ''}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error(`Error ${resp.status}`);

      const blob = await resp.blob();
      // Nombre del archivo — extraer del header o fallback
      let filename = fmt === 'xlsx' ? 'reservas.xlsx' : 'reservas.pdf';
      const cd = resp.headers.get('content-disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      if (m) filename = m[1];

      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(a.href);
    } catch (ex) {
      setErr(ex.message || 'Error');
      setTimeout(() => setErr(''), 3000);
    }
    setBusy(false);
  };

  return (
    <Tooltip title={fmt === 'xlsx' ? 'Descargar Excel' : 'Descargar PDF'}>
      <span>
        <Button
          size="small" variant="outlined"
          onClick={handleDownload} disabled={busy}
          startIcon={busy ? <CircularProgress size={14} /> : icon}
          sx={{
            textTransform: 'none', fontWeight: 700,
            borderColor: fmt === 'xlsx' ? '#16a34a' : '#dc2626',
            color: fmt === 'xlsx' ? '#166534' : '#991b1b',
            bgcolor: '#fff',
            '&:hover': {
              bgcolor: fmt === 'xlsx' ? '#dcfce7' : '#fee2e2',
              borderColor: fmt === 'xlsx' ? '#16a34a' : '#dc2626',
            },
          }}
        >
          {err || (fmt === 'xlsx' ? 'Excel' : 'PDF')}
        </Button>
      </span>
    </Tooltip>
  );
}


// ============================================================================
// HoyEmbarcamos — vista optimizada para el día del pasadía
// Salidas de HOY ordenadas por hora, con counter live y tap-to-call
// ============================================================================
function HoyEmbarcamos({ authedFetch, onAbrirDetalle }) {
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ultimaCarga, setUltimaCarga] = useState(new Date());

  const cargar = async () => {
    setLoading(true); setError('');
    try {
      const hoy = todayLocal();
      const q = new URLSearchParams({ fecha_desde: hoy, fecha_hasta: hoy });
      const d = await authedFetch(`/socios/reservas?${q}`);
      setReservas(d.reservas || []);
      setUltimaCarga(new Date());
    } catch (ex) {
      setError(ex.message || 'Error');
    }
    setLoading(false);
  };

  useEffect(() => {
    cargar();
    // Auto-refresh cada 60 segundos (útil si el socio deja abierta la pantalla)
    const timer = setInterval(cargar, 60000);
    return () => clearInterval(timer);
  }, []); // eslint-disable-line

  // Ordenar por hora de salida + estado (pendientes arriba)
  const reservasOrdenadas = useMemo(() => {
    return [...reservas].sort((a, b) => {
      // Pendientes primero, luego embarcados, luego no_show
      const orden = { pendiente: 0, embarcado: 1, no_show: 2 };
      const oa = orden[a.estado_embarque] ?? 99;
      const ob = orden[b.estado_embarque] ?? 99;
      if (oa !== ob) return oa - ob;
      // Dentro del mismo estado: por hora, luego por nombre
      const ha = (a.hora_salida || '').slice(0, 5);
      const hb = (b.hora_salida || '').slice(0, 5);
      if (ha !== hb) return ha.localeCompare(hb);
      return (a.nombre_cliente || '').localeCompare(b.nombre_cliente || '');
    });
  }, [reservas]);

  const stats = useMemo(() => {
    const s = { total: reservas.length, pax: 0, pendientes: 0, embarcados: 0, no_show: 0, pax_pendiente: 0 };
    for (const r of reservas) {
      const p = (r.adultos || 0) + (r.ninos || 0) + (r.bebes || 0);
      s.pax += p;
      if (r.estado_embarque === 'embarcado') s.embarcados += 1;
      else if (r.estado_embarque === 'no_show') s.no_show += 1;
      else { s.pendientes += 1; s.pax_pendiente += p; }
    }
    return s;
  }, [reservas]);

  const horaAhora = new Date().toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });

  return (
    <Box>
      {/* Banda de estado grande */}
      <Card sx={{
        p: 2, mb: 2, borderRadius: 3,
        background: 'linear-gradient(135deg, #0c4a6e 0%, #0e7490 100%)',
        color: '#fff',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Box>
            <Typography sx={{ fontSize: 11, color: '#bae6fd', letterSpacing: 0.5, fontWeight: 700, textTransform: 'uppercase' }}>
              Hoy embarcamos
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800 }}>
              {fmtFecha(todayLocal())}
            </Typography>
          </Box>
          <Tooltip title="Recargar">
            <IconButton onClick={cargar} sx={{ color: '#fff' }} disabled={loading}>
              {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
          <StatMini value={stats.total} label="Reservas" />
          <StatMini value={stats.pax} label="Pax total" />
          <StatMini value={stats.pendientes} sub={`${stats.pax_pendiente} pax`} label="Faltan" highlight="#fbbf24" />
          <StatMini value={stats.embarcados} label="Ya subieron" highlight="#4ade80" />
        </Box>

        {stats.no_show > 0 && (
          <Box sx={{ mt: 1.5, p: 1, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.1)' }}>
            <Typography sx={{ fontSize: 12, color: '#fecaca', fontWeight: 700 }}>
              ⚠ {stats.no_show} no-show{stats.no_show !== 1 ? 's' : ''} registrados
            </Typography>
          </Box>
        )}

        <Typography sx={{ fontSize: 10, color: '#bae6fd', mt: 1, textAlign: 'right' }}>
          Actualizado {ultimaCarga.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })} · auto-refresh cada 1 min
        </Typography>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Lista de reservas del día */}
      {loading && reservas.length === 0 ? (
        <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress /></Box>
      ) : reservasOrdenadas.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <DirectionsBoatIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#475569' }}>
            No hay reservas para hoy.
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 0.5 }}>
            Cuando entren reservas para hoy aparecerán aquí.
          </Typography>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {reservasOrdenadas.map(r => (
            <TarjetaReservaHoy key={r.id} r={r} onAbrirDetalle={onAbrirDetalle} />
          ))}
        </Box>
      )}
    </Box>
  );
}


function StatMini({ value, label, sub, highlight }) {
  return (
    <Box sx={{ textAlign: 'center', p: 1, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.1)' }}>
      <Typography sx={{ fontSize: 24, fontWeight: 800, color: highlight || '#fff', lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: 10, color: '#e0f2fe', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, mt: 0.25 }}>
        {label}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: 10, color: '#bae6fd', mt: 0.25 }}>{sub}</Typography>
      )}
    </Box>
  );
}


function TarjetaReservaHoy({ r, onAbrirDetalle }) {
  const est = ESTADO_CHIP[r.estado_embarque] || ESTADO_CHIP.pendiente;
  const esPendiente = r.estado_embarque !== 'embarcado' && r.estado_embarque !== 'no_show';
  const pax = (r.adultos || 0) + (r.ninos || 0) + (r.bebes || 0);

  const telLimpio = (r.telefono || '').replace(/[^\d+]/g, '');

  return (
    <Card sx={{
      p: 1.5, borderRadius: 3,
      borderLeft: `4px solid ${esPendiente ? '#f59e0b' : (r.estado_embarque === 'embarcado' ? '#16a34a' : '#94a3b8')}`,
      opacity: r.estado_embarque === 'no_show' ? 0.6 : 1,
      cursor: 'pointer',
      transition: 'all 0.15s',
      '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)', transform: 'translateY(-1px)' },
    }}
      onClick={() => onAbrirDetalle && onAbrirDetalle(r.id)}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }} noWrap>
              {r.nombre_cliente}
            </Typography>
            <Chip size="small" label={est.label}
              sx={{ bgcolor: est.bg, color: est.color, fontWeight: 700, fontSize: 10, height: 20 }} />
          </Box>
          <Typography sx={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace', mb: 0.5 }}>
            {r.codigo_reserva}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TodayIcon sx={{ fontSize: 14, color: '#0891b2' }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0891b2' }}>
                {fmtHora(r.hora_salida)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <GroupsIcon sx={{ fontSize: 14, color: '#475569' }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>
                {pax} pax
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                ({fmtPax(r)})
              </Typography>
            </Box>
          </Box>
          {r.requerimientos_especiales && (
            <Box sx={{ mt: 0.75, p: 0.75, bgcolor: '#fef3c7', borderRadius: 1 }}>
              <Typography sx={{ fontSize: 11, color: '#78350f', fontWeight: 600 }}>
                ⚠ {r.requerimientos_especiales}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Botón llamar — solo si está pendiente y hay teléfono */}
        {esPendiente && telLimpio && (
          <a href={`tel:${telLimpio}`}
            onClick={(e) => e.stopPropagation()}
            style={{ textDecoration: 'none', flexShrink: 0 }}>
            <Button variant="contained" size="small"
              startIcon={<PhoneIcon />}
              sx={{
                textTransform: 'none', fontWeight: 700, fontSize: 12,
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                boxShadow: '0 4px 12px rgba(245,158,11,0.35)',
                whiteSpace: 'nowrap',
                '&:hover': { background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)' },
              }}>
              Llamar
            </Button>
          </a>
        )}
      </Box>
    </Card>
  );
}


// ============================================================================
// Proximas48h — vista de hoy + próximos 2 días, agrupado por día
// ============================================================================
function Proximas48h({ authedFetch, onAbrirDetalle }) {
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true); setError('');
    const hoy = todayLocal();
    const hasta = addDays(hoy, 2);
    const q = new URLSearchParams({ fecha_desde: hoy, fecha_hasta: hasta });
    authedFetch(`/socios/reservas?${q}`)
      .then(d => { if (alive) setReservas(d.reservas || []); })
      .catch(ex => { if (alive) setError(ex.message || 'Error'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [authedFetch]);

  // Agrupar por fecha_salida
  const grupos = useMemo(() => {
    const m = new Map();
    for (const r of reservas) {
      const k = r.fecha_salida || '—';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    }
    // Ordenar cada grupo por hora y luego nombre
    for (const arr of m.values()) {
      arr.sort((a, b) => {
        const ha = (a.hora_salida || '').slice(0, 5);
        const hb = (b.hora_salida || '').slice(0, 5);
        if (ha !== hb) return ha.localeCompare(hb);
        return (a.nombre_cliente || '').localeCompare(b.nombre_cliente || '');
      });
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [reservas]);

  const hoy = todayLocal();
  const manana = addDays(hoy, 1);

  const labelDia = (iso) => {
    if (iso === hoy) return 'HOY';
    if (iso === manana) return 'MAÑANA';
    return fmtFecha(iso).toUpperCase();
  };

  const statsDia = (arr) => {
    const s = { reservas: arr.length, pax: 0, embarcados: 0, no_show: 0, pendientes: 0 };
    for (const r of arr) {
      s.pax += (r.adultos || 0) + (r.ninos || 0) + (r.bebes || 0);
      if (r.estado_embarque === 'embarcado') s.embarcados += 1;
      else if (r.estado_embarque === 'no_show') s.no_show += 1;
      else s.pendientes += 1;
    }
    return s;
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress /></Box>
      ) : grupos.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <EventNoteIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#475569' }}>
            No hay reservas en las próximas 48 horas.
          </Typography>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {grupos.map(([iso, arr]) => {
            const s = statsDia(arr);
            const esHoy = iso === hoy;
            const esManana = iso === manana;
            return (
              <Box key={iso}>
                {/* Header del día */}
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, p: 1.5,
                  borderRadius: 2,
                  background: esHoy
                    ? 'linear-gradient(135deg, #0c4a6e 0%, #0e7490 100%)'
                    : (esManana ? 'linear-gradient(135deg, #0e7490 0%, #0891b2 100%)' : '#e0f2fe'),
                  color: (esHoy || esManana) ? '#fff' : '#0c4a6e',
                }}>
                  <Box>
                    <Typography sx={{ fontSize: 11, opacity: 0.85, fontWeight: 700, letterSpacing: 0.5 }}>
                      {labelDia(iso)}
                    </Typography>
                    <Typography sx={{ fontSize: 15, fontWeight: 800 }}>
                      {fmtFecha(iso)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }} />
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <MiniStat label="Res" value={s.reservas} />
                    <MiniStat label="Pax" value={s.pax} />
                    {s.pendientes > 0 && <MiniStat label="Faltan" value={s.pendientes} accent="#fbbf24" />}
                    {s.embarcados > 0 && <MiniStat label="Subieron" value={s.embarcados} accent="#4ade80" />}
                  </Box>
                </Box>

                {/* Tarjetas del día — reutilizamos TarjetaReservaHoy */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {arr.map(r => (
                    <TarjetaReservaHoy key={r.id} r={r} onAbrirDetalle={onAbrirDetalle} />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

function MiniStat({ label, value, accent }) {
  return (
    <Box sx={{ textAlign: 'center', px: 1 }}>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color: accent || 'inherit', lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: 9, opacity: 0.8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, mt: 0.25 }}>
        {label}
      </Typography>
    </Box>
  );
}


// ============================================================================
// VistaAnalitica — dashboard de estadísticas del rango
// ============================================================================
function VistaAnalitica({ authedFetch }) {
  const now = new Date();
  const hoyISO = todayLocal();
  const hace90 = addDays(hoyISO, -90);
  const [fechaDesde, setFechaDesde] = useState(hace90);
  const [fechaHasta, setFechaHasta] = useState(hoyISO);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true); setError('');
    const q = new URLSearchParams({ fecha_desde: fechaDesde, fecha_hasta: fechaHasta });
    authedFetch(`/socios/estadisticas?${q}`)
      .then(d => { if (alive) setData(d); })
      .catch(ex => { if (alive) setError(ex.message || 'Error'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [fechaDesde, fechaHasta, authedFetch]);

  const setRangoRapido = (dias) => {
    setFechaDesde(addDays(hoyISO, -dias + 1));
    setFechaHasta(hoyISO);
  };

  return (
    <Box>
      {/* Filtro rango */}
      <Card sx={{ p: 1.5, mb: 2, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField type="date" size="small" label="Desde"
            InputLabelProps={{ shrink: true }} value={fechaDesde}
            onChange={e => setFechaDesde(e.target.value)} sx={{ minWidth: 150 }} />
          <TextField type="date" size="small" label="Hasta"
            InputLabelProps={{ shrink: true }} value={fechaHasta}
            onChange={e => setFechaHasta(e.target.value)} sx={{ minWidth: 150 }} />
          <Button size="small" variant="text" onClick={() => setRangoRapido(30)}>30 días</Button>
          <Button size="small" variant="text" onClick={() => setRangoRapido(90)}>90 días</Button>
          <Button size="small" variant="text" onClick={() => setRangoRapido(365)}>1 año</Button>
          {data && (
            <Chip size="small" label={`${data.periodo.dias} días`}
              sx={{ ml: 'auto', bgcolor: '#dcfce7', color: '#166534', fontWeight: 700 }} />
          )}
        </Box>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress /></Box>}

      {!loading && data && (
        <>
          {/* KPIs cabecera */}
          <Box sx={{ display: 'grid', gap: 1.5, mb: 2,
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' } }}>
            <KpiAnalitica title="Reservas" value={data.kpis.total_reservas} color="#0ea5e9" />
            <KpiAnalitica title="Pax totales" value={data.kpis.total_pax} color="#0891b2" />
            <KpiAnalitica title="Embarcados" value={data.kpis.embarcados} color="#16a34a" />
            <KpiAnalitica title="No-shows" value={data.kpis.no_show} color="#94a3b8" />
            <KpiAnalitica title="Tasa embarque" value={`${data.kpis.tasa_embarque_pct}%`} color="#15803d" />
            <KpiAnalitica title="Pax/reserva" value={data.kpis.pax_por_reserva_promedio} color="#7c3aed" />
          </Box>

          {/* Ocupación por día de semana */}
          <Card sx={{ p: 2, mb: 2, borderRadius: 3 }}>
            <SectionTitle title="Ocupación por día de la semana"
              subtitle="Pax promedio los días que operaste ese día de la semana" />
            <GraficoBarras
              items={data.por_dia_semana.map(d => ({
                label: d.dia,
                value: d.pax_promedio,
                sub: `${d.reservas} res · ${d.pax} pax`,
              }))}
              suffix=" pax/día"
              color="#0ea5e9"
            />
          </Card>

          {/* Grid 2 columnas: Canal + Tipo pax */}
          <Box sx={{ display: 'grid', gap: 2, mb: 2,
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            {/* Canal */}
            <Card sx={{ p: 2, borderRadius: 3 }}>
              <SectionTitle title="Por canal de reserva" />
              {data.por_canal.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
                  Sin datos de canal.
                </Typography>
              ) : (
                <GraficoBarras
                  items={data.por_canal.map(c => ({ label: c.canal, value: c.reservas }))}
                  suffix=" reservas"
                  color="#7c3aed"
                />
              )}
            </Card>

            {/* Tipo pax */}
            <Card sx={{ p: 2, borderRadius: 3 }}>
              <SectionTitle title="Composición de pasajeros" />
              {(() => {
                const t = data.por_tipo_pax;
                const total = t.adultos + t.ninos + t.bebes;
                if (total === 0) {
                  return <Typography sx={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>Sin pax.</Typography>;
                }
                return (
                  <GraficoBarras
                    items={[
                      { label: 'Adultos', value: t.adultos, sub: `${((t.adultos / total) * 100).toFixed(1)}%` },
                      { label: 'Niños',   value: t.ninos,   sub: `${((t.ninos / total) * 100).toFixed(1)}%` },
                      { label: 'Bebés',   value: t.bebes,   sub: `${((t.bebes / total) * 100).toFixed(1)}%` },
                    ]}
                    suffix=" pax"
                    color="#16a34a"
                  />
                );
              })()}
            </Card>
          </Box>

          {/* Curva de anticipación */}
          <Card sx={{ p: 2, mb: 2, borderRadius: 3 }}>
            <SectionTitle title="Curva de anticipación de venta"
              subtitle="¿Cuántos días antes de la salida reservan los clientes?" />
            <GraficoBarras
              items={data.curva_anticipacion.map(c => ({ label: c.label, value: c.reservas }))}
              suffix=" reservas"
              color="#f59e0b"
            />
          </Card>

          {/* Top nacionalidades (si hay) */}
          {data.top_nacionalidades && data.top_nacionalidades.length > 0 && (
            <Card sx={{ p: 2, mb: 2, borderRadius: 3 }}>
              <SectionTitle title="Top nacionalidades"
                subtitle="Según registros de check-in DIMAR" />
              <GraficoBarras
                items={data.top_nacionalidades.map(n => ({ label: n.nacionalidad, value: n.count }))}
                suffix=" pasajeros"
                color="#0c4a6e"
              />
            </Card>
          )}
        </>
      )}
    </Box>
  );
}


function KpiAnalitica({ title, value, color }) {
  return (
    <Card sx={{ p: 1.5, borderRadius: 3, borderLeft: `4px solid ${color}` }}>
      <Typography sx={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase',
          letterSpacing: 0.5, fontWeight: 700 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1.1, mt: 0.25 }}>
        {value}
      </Typography>
    </Card>
  );
}


function SectionTitle({ title, subtitle }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>{title}</Typography>
      {subtitle && (
        <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{subtitle}</Typography>
      )}
    </Box>
  );
}


function GraficoBarras({ items, suffix = '', color = '#0ea5e9' }) {
  // Barras horizontales. Escala relativa al máximo.
  const max = Math.max(...items.map(i => Number(i.value) || 0), 0);
  if (max === 0) {
    return (
      <Typography sx={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
        Sin datos en el rango.
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {items.map((it, i) => {
        const pct = max > 0 ? (Number(it.value) || 0) / max : 0;
        return (
          <Box key={i}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.25 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#334155', flex: 1, mr: 1,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={it.label}>
                {it.label}
              </Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color, whiteSpace: 'nowrap' }}>
                {it.value}{suffix}
                {it.sub && (
                  <Typography component="span" sx={{ fontSize: 10, color: '#94a3b8', ml: 0.5, fontWeight: 500 }}>
                    · {it.sub}
                  </Typography>
                )}
              </Typography>
            </Box>
            <Box sx={{ position: 'relative', height: 6, bgcolor: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{
                position: 'absolute', left: 0, top: 0, bottom: 0,
                width: `${pct * 100}%`, bgcolor: color,
                transition: 'width 0.3s ease',
              }} />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
